import { useEffect, useMemo, useState } from "react";
import {
  Ban,
  Barcode,
  Copy,
  Edit3,
  ExternalLink,
  Eye,
  Plus,
  Printer,
  QrCode,
  Search,
  Trash2,
} from "lucide-react";
import jsPDF from "jspdf";
import QRCode from "qrcode";
import http from "../api/http";
import Modal from "../components/Modal";
import Pagination from "../components/Pagination";
import { useServerPagination } from "../utils/usePagination";
import { consumirIntencionNuevo } from "../utils/navigationIntent";
import { crearFiltroFecha } from "../utils/dateFilters";

const fechaClave = (value) => {
  if (!value) return "";
  const coincidencia = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  if (coincidencia) return coincidencia[1];
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const fechaInput = fechaClave;

const normalizarBusqueda = (value) => String(value || "")
  .normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "")
  .toLowerCase()
  .trim();

const hoyInput = () => {
  const date = new Date();
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

const articuloInicial = () => ({
  tipoIngreso: "producto",
  producto: "",
  productoProveedorId: "",
  materialProduccionId: "",
  materialProveedorId: "",
  lotePartidaNro: "",
  fechaElaboracion: "",
  fechaVencimiento: "",
  cantidadRecibida: "",
  unidadCantidad: "",
  ubicacionAsignada: "",
  precioCompraPorUnidad: "",
  afectarCostoProveedor: false,
});

const estadoInicial = () => ({
  tipoIngreso: "producto",
  proveedor: "",
  proveedorId: "",
  proveedorSeleccion: "registrado",
  fechaIngreso: hoyInput(),
  transportista: "",
  observaciones: "",
  productos: [],
});

const estadosMercaderia = [
  { value: "ingresado", label: "Ingresado" },
  { value: "en_produccion", label: "En produccion" },
  { value: "finalizado", label: "Finalizado" },
  { value: "anulado", label: "Anulado" },
];

const etiquetaEstado = (estado) =>
  estadosMercaderia.find((item) => item.value === estado)?.label || "Ingresado";

const obtenerUnidadCantidad = (unidad) => String(unidad || "Unidad").trim() || "Unidad";

const formatearCantidad = (cantidad, unidad) =>
  `${Number(cantidad || 0).toLocaleString("es-AR")} ${obtenerUnidadCantidad(unidad)}`;
const formatearImporte = (value) => Number(value || 0).toLocaleString("es-AR", {
  style: "currency",
  currency: "ARS",
});
const unidadCostoDe = (unidad) => {
  const normalizada = String(unidad || "").toLowerCase();
  if (normalizada === "unidad") return "unidad";
  return ["ml", "l"].includes(normalizada) ? "L" : "kg";
};
const cantidadEnUnidadCosto = (cantidad, unidad) => {
  const unidadNormalizada = String(unidad || "").toLowerCase();
  if (unidadNormalizada === "mg") return Number(cantidad || 0) / 1_000_000;
  if (["g", "ml"].includes(unidadNormalizada)) return Number(cantidad || 0) / 1_000;
  return Number(cantidad || 0);
};
const precioCompraIngreso = (ingreso) => {
  const precioExplicito = Number(ingreso?.precioCompraPorUnidad || 0);
  if (precioExplicito > 0) return precioExplicito;
  return Number(
    (ingreso?.bultos || []).find((bulto) => Number(bulto.precioPorKg || 0) > 0)?.precioPorKg || 0,
  );
};
const ubicacionSugerida = (producto) => String(producto || "").trim().charAt(0).toUpperCase();

const formatearFecha = (fecha) => {
  const clave = fechaClave(fecha);
  if (!clave) return "-";
  const [anio, mes, dia] = clave.split("-");
  return `${dia}/${mes}/${anio}`;
};

const trackingUrl = (codigoTracking) =>
  `${window.location.origin}/tracking/${encodeURIComponent(codigoTracking || "")}`;

const code128Patterns = [
  "212222", "222122", "222221", "121223", "121322", "131222", "122213", "122312", "132212", "221213",
  "221312", "231212", "112232", "122132", "122231", "113222", "123122", "123221", "223211", "221132",
  "221231", "213212", "223112", "312131", "311222", "321122", "321221", "312212", "322112", "322211",
  "212123", "212321", "232121", "111323", "131123", "131321", "112313", "132113", "132311", "211313",
  "231113", "231311", "112133", "112331", "132131", "113123", "113321", "133121", "313121", "211331",
  "231131", "213113", "213311", "213131", "311123", "311321", "331121", "312113", "312311", "332111",
  "314111", "221411", "431111", "111224", "111422", "121124", "121421", "141122", "141221", "112214",
  "112412", "122114", "122411", "142112", "142211", "241211", "221114", "413111", "241112", "134111",
  "111242", "121142", "121241", "114212", "124112", "124211", "411212", "421112", "421211", "212141",
  "214121", "412121", "111143", "111341", "131141", "114113", "114311", "411113", "411311", "113141",
  "114131", "311141", "411131", "211412", "211214", "211232", "2331112",
];

const code128Codes = (value) => {
  const texto = String(value || "-")
    .split("")
    .map((char) => {
      const code = char.charCodeAt(0);
      return code >= 32 && code <= 126 ? char : "-";
    })
    .join("");
  const codigos = [104];
  let checksum = 104;

  texto.split("").forEach((char, index) => {
    const code = char.charCodeAt(0) - 32;
    codigos.push(code);
    checksum += code * (index + 1);
  });

  codigos.push(checksum % 103, 106);
  return codigos;
};

const codigoBarrasDataUrl = (value) => {
  const codigos = code128Codes(value);

  const modulo = 2;
  const alto = 58;
  const margen = 12;
  const anchoBarras = codigos.reduce(
    (total, code) => total + code128Patterns[code].split("").reduce((sum, digit) => sum + Number(digit), 0) * modulo,
    0,
  );
  let x = margen;
  const barras = [];

  codigos.forEach((code) => {
    code128Patterns[code].split("").forEach((digit, index) => {
      const ancho = Number(digit) * modulo;
      if (index % 2 === 0) {
        barras.push(`<rect x="${x}" y="0" width="${ancho}" height="${alto}" fill="#111"/>`);
      }
      x += ancho;
    });
  });

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${anchoBarras + margen * 2}" height="${alto}" viewBox="0 0 ${anchoBarras + margen * 2} ${alto}">${barras.join("")}</svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

const dibujarCodigoBarrasPDF = (doc, value, x, y, modulo = 0.28, alto = 12) => {
  let cursor = x;
  code128Codes(value).forEach((code) => {
    code128Patterns[code].split("").forEach((digit, index) => {
      const ancho = Number(digit) * modulo;
      if (index % 2 === 0) doc.rect(cursor, y, ancho, alto, "F");
      cursor += ancho;
    });
  });
};

const abrirPdfRotulo = async (ingreso) => {
  const codigo = ingreso.codigoTracking || "-";
  const cantidad = Number(ingreso.cantidadRecibida || 0);
  const unidad = obtenerUnidadCantidad(ingreso.unidadCantidad);
  const qrDataUrl = await QRCode.toDataURL(trackingUrl(codigo), { margin: 1, width: 180 });
  const doc = new jsPDF({ unit: "mm", format: [100, 70], orientation: "landscape" });
  const dato = (label, value, x, y) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(5.8);
    doc.setTextColor(85);
    doc.text(label.toUpperCase(), x, y);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.4);
    doc.setTextColor(20);
    doc.text(String(value || "-"), x, y + 3.8, { maxWidth: 27 });
  };

  doc.setProperties({ title: `Rotulo ${codigo}` });
  doc.setDrawColor(20);
  doc.setFillColor(255, 255, 255);
  doc.rect(3, 3, 94, 64);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(6);
  doc.setTextColor(85);
  doc.text("INGRESO DE ENVASES / PRODUCTOS", 6, 8);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  doc.setTextColor(10);
  doc.text(codigo, 6, 14);
  doc.setFontSize(7.5);
  doc.text("ALMACENAMIENTO", 77, 10, { align: "center" });

  doc.setDrawColor(20);
  doc.rect(6, 17, 88, 10);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(5.8);
  doc.setTextColor(85);
  doc.text(ingreso.tipoIngreso === "material" ? "MATERIAL" : "PRODUCTO", 8, 21);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(10);
  doc.setTextColor(10);
  doc.text(ingreso.producto || "-", 8, 25, { maxWidth: 83 });

  dato("Proveedor", ingreso.proveedor, 6, 33);
  dato("Lote / partida", ingreso.lotePartidaNro, 37, 33);
  dato("Ingreso", formatearFecha(ingreso.fechaIngreso), 68, 33);
  dato("Elaboracion", formatearFecha(ingreso.fechaElaboracion), 6, 42);
  dato("Vencimiento", formatearFecha(ingreso.fechaVencimiento), 37, 42);
  dato("Ubicacion", ingreso.ubicacionAsignada || "-", 68, 42);
  dato("Cantidad", cantidad.toLocaleString("es-AR"), 6, 51);
  dato("Unidad", unidad, 37, 51);

  doc.setFillColor(0, 0, 0);
  dibujarCodigoBarrasPDF(doc, codigo, 6, 58, 0.24, 6);
  doc.addImage(qrDataUrl, "PNG", 80, 53, 13, 13);

  if (typeof doc.autoPrint === "function") doc.autoPrint();
  window.open(doc.output("bloburl"), "_blank", "noopener,noreferrer");
};

export default function Mercaderia() {
  const [ingresos, setIngresos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [formulario, setFormulario] = useState(estadoInicial);
  const [productoBorrador, setProductoBorrador] = useState(articuloInicial);
  const [ingresoEditando, setIngresoEditando] = useState(null);
  const [ingresoDetalle, setIngresoDetalle] = useState(null);
  const [trackingSeleccionado, setTrackingSeleccionado] = useState(null);
  const [rotuloSeleccionado, setRotuloSeleccionado] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [rotuloQrDataUrl, setRotuloQrDataUrl] = useState("");
  const [modalAbierto, setModalAbierto] = useState(false);
  const [trackingModalAbierto, setTrackingModalAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");
  const [busqueda, setBusqueda] = useState("");
  const [buscarFueraFecha, setBuscarFueraFecha] = useState(false);
  const [filtroFecha, setFiltroFecha] = useState(crearFiltroFecha);
  const [filtroProveedor, setFiltroProveedor] = useState("");
  const [filtroProducto, setFiltroProducto] = useState("");
  const paginacion = useServerPagination();

  const obtenerIngresos = async () => {
    try {
      setCargando(true);
      setError("");
      const { data } = await http.get("/mercaderia", {
        params: {
          q: busqueda.trim() || undefined,
          proveedor: filtroProveedor.trim() || undefined,
          producto: filtroProducto.trim() || undefined,
          buscarFueraFecha: buscarFueraFecha || undefined,
          periodo: filtroFecha.tipo,
          dia: filtroFecha.dia || undefined,
          semana: filtroFecha.semana || undefined,
          mes: filtroFecha.mes || undefined,
          anio: filtroFecha.anio || undefined,
          desde: filtroFecha.desde || undefined,
          hasta: filtroFecha.hasta || undefined,
          page: paginacion.page,
          limit: paginacion.pageSize,
        },
      });
      setIngresos(Array.isArray(data?.items) ? data.items : []);
      paginacion.applyResponse(data);
    } catch (err) {
      setError(err.response?.data?.error || "No se pudieron cargar los ingresos.");
    } finally {
      setCargando(false);
    }
  };

  const obtenerProveedores = async () => {
    try {
      const { data } = await http.get("/proveedores?activo=true");
      setProveedores(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.response?.data?.error || "No se pudieron cargar los proveedores.");
    }
  };

  useEffect(() => {
    obtenerProveedores();
    if (consumirIntencionNuevo()) setModalAbierto(true);
  }, []);

  useEffect(() => {
    const timeoutId = window.setTimeout(obtenerIngresos, 250);
    return () => window.clearTimeout(timeoutId);
  }, [
    busqueda,
    buscarFueraFecha,
    filtroFecha,
    filtroProducto,
    filtroProveedor,
    paginacion.page,
    paginacion.pageSize,
  ]);

  const proveedorSeleccionado = useMemo(
    () => proveedores.find((proveedor) => proveedor._id === formulario.proveedorId),
    [proveedores, formulario.proveedorId],
  );

  const productosProveedor = useMemo(() => {
    const productos = proveedorSeleccionado?.productos || [];
    return productos.filter((producto) => producto.activo !== false);
  }, [proveedorSeleccionado]);
  const materialesProveedor = useMemo(() => {
    const materiales = proveedorSeleccionado?.materiales || [];
    return materiales.filter((material) => material.activo !== false);
  }, [proveedorSeleccionado]);
  const articulosProveedor = formulario.tipoIngreso === "material" ? materialesProveedor : productosProveedor;
  const articuloProveedorId = formulario.tipoIngreso === "material"
    ? productoBorrador.materialProveedorId
    : productoBorrador.productoProveedorId;
  const costoBorrador = cantidadEnUnidadCosto(
    productoBorrador.cantidadRecibida,
    productoBorrador.unidadCantidad,
  ) * Number(productoBorrador.precioCompraPorUnidad || 0);
  const costoTotalFormulario = useMemo(
    () => formulario.productos.reduce(
      (total, item) => total + cantidadEnUnidadCosto(
        item.cantidadRecibida,
        item.unidadCantidad,
      ) * Number(item.precioCompraPorUnidad || 0),
      0,
    ),
    [formulario.productos],
  );

  const proveedoresFiltro = useMemo(
    () => [...new Set(
      proveedores.map((proveedor) => String(
        proveedor.razonSocial || proveedor.nombreFantasia || "",
      ).trim()).filter(Boolean),
    )].sort((a, b) => a.localeCompare(b, "es")),
    [proveedores],
  );

  const productosFiltro = useMemo(
    () => [...new Set(
      proveedores.flatMap((proveedor) => [
        ...(proveedor.productos || []).map((item) => item.nombre),
        ...(proveedor.materiales || []).map((item) => item.nombre),
      ]).map((nombre) => String(nombre || "").trim()).filter(Boolean),
    )].sort((a, b) => a.localeCompare(b, "es")),
    [proveedores],
  );

  const limpiarMensajes = () => {
    setMensaje("");
    setError("");
  };

  const abrirCrear = () => {
    limpiarMensajes();
    setIngresoEditando(null);
    setFormulario(estadoInicial());
    setProductoBorrador(articuloInicial());
    setModalAbierto(true);
  };

  const abrirEditar = (ingreso) => {
    limpiarMensajes();
    setIngresoEditando(ingreso);
    const productos = (ingreso.productos?.length ? ingreso.productos : [ingreso]).map((item) => ({
      ...articuloInicial(),
      ...item,
      productoProveedorId: item.productoProveedorId || "",
      materialProduccionId: item.materialProduccionId || "",
      materialProveedorId: item.materialProveedorId || "",
      fechaElaboracion: fechaInput(item.fechaElaboracion),
      fechaVencimiento: fechaInput(item.fechaVencimiento),
      cantidadRecibida: item.cantidadRecibida || "",
      unidadCantidad: obtenerUnidadCantidad(item.unidadCantidad),
      precioCompraPorUnidad: precioCompraIngreso(item),
    }));
    setFormulario({
      ...estadoInicial(),
      ...ingreso,
      proveedorId:
        typeof ingreso.proveedorId === "string"
          ? ingreso.proveedorId
          : ingreso.proveedorId?._id || "",
      fechaIngreso: fechaInput(ingreso.fechaIngreso) || hoyInput(),
      proveedorSeleccion: ingreso.proveedorId ? "registrado" : ingreso.proveedorSeleccion || "otros",
      productos,
    });
    setProductoBorrador(articuloInicial());
    setModalAbierto(true);
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setIngresoEditando(null);
    setFormulario(estadoInicial());
    setProductoBorrador(articuloInicial());
    limpiarMensajes();
  };

  const cerrarTrackingModal = () => {
    setTrackingModalAbierto(false);
    setTrackingSeleccionado(null);
    setQrDataUrl("");
  };

  const cerrarRotuloModal = () => {
    setRotuloSeleccionado(null);
    setRotuloQrDataUrl("");
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    limpiarMensajes();

    setFormulario((prev) => {
      if (name === "tipoIngreso") {
        setProductoBorrador({ ...articuloInicial(), tipoIngreso: value });
        return {
          ...prev,
          tipoIngreso: value,
        };
      }

      if (name === "proveedorSeleccion") {
        setProductoBorrador({ ...articuloInicial(), tipoIngreso: prev.tipoIngreso });
        return {
          ...prev,
          proveedorSeleccion: value,
          proveedorId: "",
          proveedor: "",
          productos: [],
        };
      }

      if (name === "proveedorId") {
        setProductoBorrador({ ...articuloInicial(), tipoIngreso: prev.tipoIngreso });
        const proveedor = proveedores.find((item) => item._id === value);
        return {
          ...prev,
          proveedorId: value,
          proveedor: proveedor ? proveedor.razonSocial || proveedor.nombreFantasia || "" : "",
          productos: [],
        };
      }

      return {
        ...prev,
        [name]: value,
      };
    });
  };

  const seleccionarProveedor = (value) => {
    limpiarMensajes();
    const proveedor = proveedores.find((item) => item._id === value);
    setProductoBorrador({ ...articuloInicial(), tipoIngreso: formulario.tipoIngreso });
    setFormulario((prev) => ({
      ...prev,
      proveedorId: value,
      proveedor: proveedor ? proveedor.razonSocial || proveedor.nombreFantasia || "" : "",
      productos: [],
    }));
  };

  const seleccionarArticulo = (value) => {
    limpiarMensajes();
    const articulo = articulosProveedor.find((item) => item._id === value);
    const nombreArticulo = articulo?.nombre || "";
    const esMaterial = formulario.tipoIngreso === "material";
    const precio = Number(articulo?.precioCompraPorUnidad ?? articulo?.precioPorKg ?? 0);
    setProductoBorrador((prev) => ({
      ...prev,
      tipoIngreso: formulario.tipoIngreso,
      productoProveedorId: esMaterial ? "" : value,
      materialProveedorId: esMaterial ? value : "",
      materialProduccionId: esMaterial ? articulo?.materialProduccionId || "" : "",
      producto: nombreArticulo,
      ubicacionAsignada: prev.ubicacionAsignada || ubicacionSugerida(nombreArticulo),
      unidadCantidad: articulo?.unidadMedida || (esMaterial ? "unidad" : ""),
      precioCompraPorUnidad: precio,
    }));
  };

  const actualizarBorrador = (name, value) => {
    setProductoBorrador((prev) => ({ ...prev, [name]: value }));
  };

  const agregarArticulo = () => {
    if (!articuloProveedorId) return setError(`Selecciona ${formulario.tipoIngreso === "material" ? "el material" : "la materia prima"}.`);
    if (Number(productoBorrador.cantidadRecibida || 0) <= 0) return setError("Carga una cantidad mayor a cero.");
    limpiarMensajes();
    setFormulario((prev) => ({ ...prev, productos: [...prev.productos, { ...productoBorrador }] }));
    setProductoBorrador({ ...articuloInicial(), tipoIngreso: formulario.tipoIngreso });
  };

  const actualizarArticulo = (index, name, value) => {
    setFormulario((prev) => ({
      ...prev,
      productos: prev.productos.map((item, itemIndex) => itemIndex === index ? { ...item, [name]: value } : item),
    }));
  };

  const quitarArticulo = (index) => {
    setFormulario((prev) => ({ ...prev, productos: prev.productos.filter((_, itemIndex) => itemIndex !== index) }));
  };

  const prepararPayload = () => ({
    ...formulario,
    proveedorId: formulario.proveedorSeleccion === "registrado" ? formulario.proveedorId || null : null,
    proveedorSeleccion: formulario.proveedorSeleccion,
    origen: "",
    fechaIngreso: formulario.fechaIngreso || null,
    productos: formulario.productos.map((item) => ({
      ...item,
      cantidadRecibida: Number(item.cantidadRecibida || 0),
      precioCompraPorUnidad: Number(item.precioCompraPorUnidad || 0),
      fechaElaboracion: item.fechaElaboracion || null,
      fechaVencimiento: item.fechaVencimiento || null,
      bultos: [],
    })),
  });

  const validarFormulario = () => {
    if (formulario.proveedorSeleccion === "registrado" && !formulario.proveedorId) {
      setError("Selecciona un proveedor registrado.");
      return false;
    }

    if (!formulario.proveedor.trim() && formulario.proveedorSeleccion === "otros") {
      setError("El proveedor es obligatorio.");
      return false;
    }

    if (!formulario.productos.length) {
      setError("Agrega al menos una materia prima o material al ingreso.");
      return false;
    }

    return true;
  };

  const guardarIngreso = async (event) => {
    event.preventDefault();

    if (!validarFormulario()) return;

    try {
      setCargando(true);
      setError("");
      const payload = prepararPayload();

      if (ingresoEditando) {
        const { data } = await http.put(`/mercaderia/${ingresoEditando._id}`, payload);
        setMensaje(`Ingreso actualizado. Tracking: ${data.codigoTracking || ingresoEditando.codigoTracking}`);
      } else {
        const { data } = await http.post("/mercaderia", payload);
        setMensaje(`Ingreso registrado con ${data.cantidadProductos || formulario.productos.length} articulos. Codigo: ${data.ingresoGrupoCodigo || data.codigoTracking}`);
      }

      window.dispatchEvent(new Event("notificaciones:actualizar"));
      cerrarModal();
      await obtenerIngresos();
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo guardar el ingreso.");
    } finally {
      setCargando(false);
    }
  };

  const anularIngreso = async (ingreso) => {
    if (!window.confirm(`Seguro que queres anular el ingreso ${ingreso.codigoTracking}?`)) return;

    try {
      setCargando(true);
      setError("");
      await http.delete(`/mercaderia/${ingreso._id}`);
      window.dispatchEvent(new Event("notificaciones:actualizar"));
      setMensaje("Ingreso anulado correctamente.");
      await obtenerIngresos();
    } catch (err) {
      setError(err.response?.data?.error || "No se pudo anular el ingreso.");
    } finally {
      setCargando(false);
    }
  };

  const copiarTracking = async (codigoTracking) => {
    try {
      await navigator.clipboard.writeText(trackingUrl(codigoTracking));
      setMensaje("Link de tracking copiado.");
    } catch {
      setError("No se pudo copiar el link de tracking.");
    }
  };

  const abrirTracking = async (ingreso) => {
    setTrackingSeleccionado(ingreso);
    setQrDataUrl("");
    setTrackingModalAbierto(true);

    try {
      const dataUrl = await QRCode.toDataURL(trackingUrl(ingreso.codigoTracking), {
        margin: 2,
        width: 190,
      });
      setQrDataUrl(dataUrl);
    } catch {
      setQrDataUrl("");
    }
  };

  const abrirRotulos = async (ingreso) => {
    setRotuloSeleccionado(ingreso);
    setRotuloQrDataUrl("");

    try {
      const dataUrl = await QRCode.toDataURL(trackingUrl(ingreso.codigoTracking), {
        margin: 1,
        width: 150,
      });
      setRotuloQrDataUrl(dataUrl);
    } catch {
      setRotuloQrDataUrl("");
    }
  };

  const imprimirRotulo = async () => {
    try {
      await abrirPdfRotulo(rotuloSeleccionado);
    } catch {
      setError("No se pudo generar el rotulo para imprimir.");
    }
  };

  return (
    <div>
      <div className="page-header actions-only">
        <div className="header-actions">
          <button className="btn btn-primary" onClick={abrirCrear}>
            <Plus size={18} />
            Nuevo ingreso
          </button>
        </div>
      </div>

      {mensaje && <div className="alert alert-ok">{mensaje}</div>}
      {error && !modalAbierto && !trackingModalAbierto && !rotuloSeleccionado && (
        <div className="alert alert-error">{error}</div>
      )}

      <section className="panel">
        <div className="toolbar">
          <div>
            <h2>Ingresos registrados</h2>
            <p>Total: {paginacion.totalItems}</p>
          </div>
          <div className="toolbar-filters">
            <div className="ingress-entity-filters" aria-label="Filtros de proveedor y producto">
              <SearchFilter
                value={filtroProveedor}
                onChange={(value) => {
                  setFiltroProveedor(value);
                  paginacion.resetPage();
                }}
                options={proveedoresFiltro}
                placeholder="Buscar proveedor..."
                ariaLabel="Filtrar por proveedor"
                todosLabel="Todos los proveedores"
              />
              <SearchFilter
                value={filtroProducto}
                onChange={(value) => {
                  setFiltroProducto(value);
                  paginacion.resetPage();
                }}
                options={productosFiltro}
                placeholder="Buscar materia prima o material..."
                ariaLabel="Filtrar por materia prima o material"
                todosLabel="Todas las materias primas y materiales"
              />
            </div>
            <div className="finalized-filter" aria-label="Filtro por fecha de ingreso">
              <select
                aria-label="Tipo de filtro de fecha"
                value={filtroFecha.tipo}
                onChange={(event) => {
                  setFiltroFecha((actual) => ({ ...actual, tipo: event.target.value }));
                  paginacion.resetPage();
                }}
              >
                <option value="todos">Todos</option>
                <option value="dia">Por dia</option>
                <option value="semana">Por semana</option>
                <option value="mes">Por mes</option>
                <option value="anio">Por año</option>
                <option value="rango">Por rango</option>
              </select>
              {filtroFecha.tipo === "todos" ? null : filtroFecha.tipo === "rango" ? (
                <>
                  <input
                    aria-label="Fecha desde"
                    type="date"
                    value={filtroFecha.desde}
                    onChange={(event) => {
                      setFiltroFecha((actual) => ({ ...actual, desde: event.target.value }));
                      paginacion.resetPage();
                    }}
                  />
                  <input
                    aria-label="Fecha hasta"
                    type="date"
                    value={filtroFecha.hasta}
                    onChange={(event) => {
                      setFiltroFecha((actual) => ({ ...actual, hasta: event.target.value }));
                      paginacion.resetPage();
                    }}
                  />
                </>
              ) : filtroFecha.tipo === "semana" ? (
                <input
                  aria-label="Semana de ingreso"
                  type="week"
                  value={filtroFecha.semana}
                  onChange={(event) => {
                    setFiltroFecha((actual) => ({ ...actual, semana: event.target.value }));
                    paginacion.resetPage();
                  }}
                />
              ) : filtroFecha.tipo === "mes" ? (
                <input
                  aria-label="Mes de ingreso"
                  type="month"
                  value={filtroFecha.mes}
                  onChange={(event) => {
                    setFiltroFecha((actual) => ({ ...actual, mes: event.target.value }));
                    paginacion.resetPage();
                  }}
                />
              ) : filtroFecha.tipo === "anio" ? (
                <input
                  aria-label="Año de ingreso"
                  type="text"
                  inputMode="decimal"
                  min="2000"
                  max="2100"
                  step="1"
                  value={filtroFecha.anio}
                  onChange={(event) => {
                    setFiltroFecha((actual) => ({ ...actual, anio: event.target.value }));
                    paginacion.resetPage();
                  }}
                />
              ) : (
                <input
                  aria-label="Dia de ingreso"
                  type="date"
                  value={filtroFecha.dia}
                  onChange={(event) => {
                    setFiltroFecha((actual) => ({ ...actual, dia: event.target.value }));
                    paginacion.resetPage();
                  }}
                />
              )}
            </div>
            <form
              className="code-search"
              onSubmit={(event) => {
                event.preventDefault();
                paginacion.resetPage();
                setBuscarFueraFecha(Boolean(busqueda.trim()));
              }}
            >
              <div className="search">
                <Search size={17} />
                <input
                  aria-label="Buscar ingreso por codigo"
                  placeholder="Buscar por codigo..."
                  value={busqueda}
                  onChange={(event) => {
                    setBusqueda(event.target.value);
                    setBuscarFueraFecha(false);
                    paginacion.resetPage();
                  }}
                />
              </div>
              <button className="btn" type="submit">
                <Search size={16} />
                Buscar
              </button>
            </form>
          </div>
        </div>

        {cargando ? (
          <div className="loading-state">Cargando ingresos...</div>
        ) : ingresos.length === 0 ? (
          <div className="empty-state">No hay ingresos para mostrar.</div>
        ) : (
          <>
            <div className="table-wrap">
              <table>
              <thead>
                <tr>
                  <th>Codigo de ingreso</th>
                  <th>Proveedor</th>
                  <th>Ingreso</th>
                  <th>Vencimiento</th>
                  <th>Cantidad</th>
                  <th>Costo ingreso</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {ingresos.map((ingreso) => (
                  <tr key={ingreso._id}>
                    <td><strong>{ingreso.ingresoGrupoCodigo || ingreso.codigoTracking || "-"}</strong></td>
                    <td>{ingreso.proveedor || "-"}</td>
                    <td>{formatearFecha(ingreso.fechaIngreso)}</td>
                    <td>{formatearFecha(ingreso.fechaVencimiento)}</td>
                    <td>{ingreso.cantidadProductos > 1 ? `${ingreso.cantidadProductos} articulos` : formatearCantidad(ingreso.cantidadRecibida, ingreso.unidadCantidad)}</td>
                    <td><strong>{formatearImporte(ingreso.costoTotal)}</strong></td>
                    <td>
                      <div className="row-actions">
                        <button
                          className="btn action-icon-button"
                          type="button"
                          title="Detalle"
                          aria-label="Detalle"
                          onClick={() => setIngresoDetalle(ingreso)}
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          className="btn action-icon-button"
                          type="button"
                          title="Editar"
                          aria-label="Editar"
                          onClick={() => abrirEditar(ingreso)}
                        >
                          <Edit3 size={16} />
                        </button>
                        <button
                          className="btn btn-danger action-icon-button"
                          type="button"
                          title="Anular"
                          aria-label="Anular"
                          disabled={ingreso.estado === "anulado"}
                          onClick={() => anularIngreso(ingreso)}
                        >
                          <Ban size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              </table>
            </div>
            <Pagination
              page={paginacion.page}
              pageSize={paginacion.pageSize}
              totalItems={paginacion.totalItems}
              totalPages={paginacion.totalPages}
              onPageChange={paginacion.setPage}
              onPageSizeChange={paginacion.setPageSize}
            />
          </>
        )}
      </section>

      <Modal
        abierto={modalAbierto}
        titulo={ingresoEditando ? "Editar ingreso" : "Registrar ingreso"}
        subtitulo={
          ingresoEditando
            ? `Tracking: ${ingresoEditando.codigoTracking || ""}`
            : "Carga los datos de recepcion y trazabilidad."
        }
        size="full"
        onClose={cerrarModal}
        footer={
          <>
            <button className="btn" type="button" onClick={cerrarModal}>
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              form="mercaderia-form"
              type="submit"
              disabled={cargando}
            >
              {ingresoEditando ? "Actualizar ingreso" : "Guardar ingreso"}
            </button>
          </>
        }
      >
        {error && <div className="alert alert-error">{error}</div>}
        <form id="mercaderia-form" onSubmit={guardarIngreso}>
          <section className="form-section">
            <h3>Proveedor e ingreso</h3>
            <div className="form-grid">
              <SelectField
                label="Tipo de ingreso"
                name="tipoIngreso"
                value={formulario.tipoIngreso}
                onChange={handleChange}
                disabled={Boolean(ingresoEditando)}
                options={[
                  { value: "producto", label: "Materia prima" },
                  { value: "material", label: "Material de producción" },
                ]}
              />
              <SearchableSelect
                label="Proveedor *"
                value={formulario.proveedorId}
                onChange={seleccionarProveedor}
                disabled={Boolean(ingresoEditando)}
                options={proveedores.map((proveedor) => ({
                  value: proveedor._id,
                  label: proveedor.razonSocial || proveedor.nombreFantasia,
                }))}
                placeholder="Buscar..."
              />
              <Field label="Fecha de ingreso" type="date" name="fechaIngreso" value={formulario.fechaIngreso} onChange={handleChange} />
              <Field label="Transportista" name="transportista" value={formulario.transportista} onChange={handleChange} />
            </div>
          </section>

          <div className="ingreso-products-heading">
            <div>
              <h3>{formulario.tipoIngreso === "material" ? "Materiales recibidos" : "Materias primas recibidas"}</h3>
              <p>Completa el renglon y presiona Agregar para incorporarlo al ingreso.</p>
            </div>
          </div>

          {!ingresoEditando && (
            <section className="ingreso-builder">
              <div className="ingreso-builder-header">
                <h4>Cargar {formulario.tipoIngreso === "material" ? "material" : "materia prima"}</h4>
                <span>Este renglon se limpia despues de agregarlo.</span>
              </div>
              <div className="ingreso-item-grid">
                <SearchableSelect label={formulario.tipoIngreso === "material" ? "Material *" : "Materia prima *"} value={articuloProveedorId} onChange={seleccionarArticulo} options={articulosProveedor.map((articulo) => ({ value: articulo._id, label: articulo.nombre }))} placeholder="Buscar..." disabled={!formulario.proveedorId} />
                <Field label="Lote / partida" value={productoBorrador.lotePartidaNro} onChange={(event) => actualizarBorrador("lotePartidaNro", event.target.value)} />
                <Field label="Elaboracion" type="date" value={productoBorrador.fechaElaboracion} onChange={(event) => actualizarBorrador("fechaElaboracion", event.target.value)} />
                <Field label="Vencimiento" type="date" value={productoBorrador.fechaVencimiento} onChange={(event) => actualizarBorrador("fechaVencimiento", event.target.value)} />
                <Field label="Cantidad *" type="text" inputMode="decimal" step="0.001" min="0" value={productoBorrador.cantidadRecibida} onChange={(event) => actualizarBorrador("cantidadRecibida", event.target.value)} />
                <Field label="Unidad" value={productoBorrador.unidadCantidad} disabled />
                <Field label={`Costo por 1 ${unidadCostoDe(productoBorrador.unidadCantidad)}`} type="text" inputMode="decimal" step="0.01" min="0" value={productoBorrador.precioCompraPorUnidad} onChange={(event) => actualizarBorrador("precioCompraPorUnidad", event.target.value)} />
                <Field label="Costo total" value={formatearImporte(costoBorrador)} disabled />
                <Field label="Ubicacion" value={productoBorrador.ubicacionAsignada} onChange={(event) => actualizarBorrador("ubicacionAsignada", event.target.value)} />
                <label className="checkbox-field ingreso-affect-check"><input type="checkbox" checked={productoBorrador.afectarCostoProveedor} onChange={(event) => actualizarBorrador("afectarCostoProveedor", event.target.checked)} /><span>Afectar costo del proveedor</span></label>
                <button className="btn btn-primary ingreso-add-button" type="button" onClick={agregarArticulo} title="Agregar al ingreso"><Plus size={18} /><span>Agregar</span></button>
              </div>
            </section>
          )}

          <section className="ingreso-added-section">
            <div className="ingreso-added-heading">
              <h3>{formulario.tipoIngreso === "material" ? "Materiales agregados" : "Materias primas agregadas"}</h3>
              <span>{formulario.productos.length} articulo(s)</span>
            </div>
            {!formulario.productos.length ? <div className="empty-inline">Todavia no agregaste articulos.</div> : (
              <div className="table-wrap ingreso-items-table">
                <table>
                  <thead><tr><th>N.º</th><th>Articulo</th><th>Lote</th><th>Elaboracion / vencimiento</th><th>Cantidad</th><th>Costo unitario</th><th>Afecta costo</th><th>Ubicacion</th><th>Costo total</th><th>Quitar</th></tr></thead>
                  <tbody>{formulario.productos.map((item, index) => (
                    <tr key={item._id || `${item.productoProveedorId || item.materialProveedorId}-${index}`}>
                      <td className="ingreso-index"><strong>{index + 1}</strong></td>
                      <td><input className="ingreso-product-readonly" value={item.producto} readOnly aria-label={`Articulo ${index + 1}`} /></td>
                      <td><input value={item.lotePartidaNro || ""} onChange={(event) => actualizarArticulo(index, "lotePartidaNro", event.target.value)} /></td>
                      <td><div className="ingreso-date-pair"><input type="date" value={fechaInput(item.fechaElaboracion)} onChange={(event) => actualizarArticulo(index, "fechaElaboracion", event.target.value)} /><input type="date" value={fechaInput(item.fechaVencimiento)} onChange={(event) => actualizarArticulo(index, "fechaVencimiento", event.target.value)} /></div></td>
                      <td><div className="ingreso-quantity"><input type="text" inputMode="decimal" min="0" step="0.001" value={item.cantidadRecibida} onChange={(event) => actualizarArticulo(index, "cantidadRecibida", event.target.value)} /><span>{item.unidadCantidad}</span></div></td>
                      <td><input type="text" inputMode="decimal" min="0" step="0.01" value={item.precioCompraPorUnidad} onChange={(event) => actualizarArticulo(index, "precioCompraPorUnidad", event.target.value)} /></td>
                      <td><input type="checkbox" checked={Boolean(item.afectarCostoProveedor)} onChange={(event) => actualizarArticulo(index, "afectarCostoProveedor", event.target.checked)} aria-label={`Afectar costo de ${item.producto}`} /></td>
                      <td><input value={item.ubicacionAsignada || ""} onChange={(event) => actualizarArticulo(index, "ubicacionAsignada", event.target.value)} /></td>
                      <td className="ingreso-total-cell"><strong>{formatearImporte(cantidadEnUnidadCosto(item.cantidadRecibida, item.unidadCantidad) * Number(item.precioCompraPorUnidad || 0))}</strong></td>
                      <td><button className="btn btn-danger action-icon-button" type="button" title="Quitar" aria-label="Quitar" disabled={Boolean(ingresoEditando)} onClick={() => quitarArticulo(index)}><Trash2 size={16} /></button></td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </section>

          <section className="ingreso-cost-summary" aria-label="Resumen de costos del ingreso">
            <div>
              <span>Articulos recibidos</span>
              <strong>{formulario.productos.length}</strong>
            </div>
            <div className="ingreso-cost-total">
              <span>Costo total del ingreso</span>
              <strong>{formatearImporte(costoTotalFormulario)}</strong>
            </div>
          </section>

          <section className="form-section">
            <h3>Observaciones</h3>
            <Textarea label="Observaciones" name="observaciones" value={formulario.observaciones} onChange={handleChange} />
          </section>
        </form>
      </Modal>

      <Modal
        abierto={Boolean(ingresoDetalle)}
        titulo="Detalle del ingreso"
        subtitulo={ingresoDetalle?.codigoTracking || ""}
        size="full"
        onClose={() => setIngresoDetalle(null)}
        footer={
          <button className="btn" type="button" onClick={() => setIngresoDetalle(null)}>
            Cerrar
          </button>
        }
      >
        {ingresoDetalle && (
          <div className="order-detail">
            <section className="order-detail-summary">
              <DetalleDato label="Tracking" value={ingresoDetalle.codigoTracking} />
              <DetalleDato label="Articulos" value={ingresoDetalle.cantidadProductos || 1} />
              <DetalleDato label="Proveedor" value={ingresoDetalle.proveedor} />
              <DetalleDato label="Estado" value={etiquetaEstado(ingresoDetalle.estado)} />
              <DetalleDato label="Fecha de ingreso" value={formatearFecha(ingresoDetalle.fechaIngreso)} />
              <DetalleDato label="Costo total del ingreso" value={formatearImporte(ingresoDetalle.costoTotal)} />
              <DetalleDato label="Transportista" value={ingresoDetalle.transportista} />
            </section>
            <section className="order-detail-notes">
              <h3>Materias primas y materiales</h3>
              <div className="table-wrap ingreso-detail-table">
                <table>
                  <thead><tr><th>Articulo</th><th>Tipo</th><th>Lote</th><th>Vencimiento</th><th>Cantidad</th><th>Costo unitario</th><th>Costo total</th><th>Ubicacion</th></tr></thead>
                  <tbody>{(ingresoDetalle.productos?.length ? ingresoDetalle.productos : [ingresoDetalle]).map((item) => (
                    <tr key={item._id || item.codigoTracking}>
                      <td><strong>{item.producto}</strong></td><td>{item.tipoIngreso === "material" ? "Material" : "Materia prima"}</td><td>{item.lotePartidaNro || "-"}</td><td>{formatearFecha(item.fechaVencimiento)}</td><td>{formatearCantidad(item.cantidadRecibida, item.unidadCantidad)}</td><td>{formatearImporte(precioCompraIngreso(item))}</td><td>{formatearImporte(item.costoTotal)}</td><td>{item.ubicacionAsignada || "-"}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </section>
            <section className="order-detail-notes">
              <h3>Observaciones</h3>
              <p>{ingresoDetalle.observaciones || "Sin observaciones."}</p>
            </section>
          </div>
        )}
      </Modal>

      <Modal
        abierto={trackingModalAbierto}
        titulo="Tracking de mercaderia"
        subtitulo={trackingSeleccionado?.codigoTracking || ""}
        size="small"
        onClose={cerrarTrackingModal}
        footer={
          trackingSeleccionado && (
            <>
              <button
                className="btn"
                type="button"
                onClick={() => copiarTracking(trackingSeleccionado.codigoTracking)}
              >
                <Copy size={16} />
                Copiar link
              </button>
              <a
                className="btn btn-primary"
                href={trackingUrl(trackingSeleccionado.codigoTracking)}
                target="_blank"
                rel="noreferrer"
              >
                <ExternalLink size={16} />
                Abrir
              </a>
            </>
          )
        }
      >
        {trackingSeleccionado && (
          <div className="tracking-share">
            <div className="tracking-share-code">
              <QrCode size={18} />
              <strong>{trackingSeleccionado.codigoTracking}</strong>
            </div>
            {qrDataUrl ? (
              <img src={qrDataUrl} alt="QR tracking" />
            ) : (
              <div className="empty-inline">Generando QR...</div>
            )}
            <div className="readonly-link">{trackingUrl(trackingSeleccionado.codigoTracking)}</div>
          </div>
        )}
      </Modal>

      <Modal
        abierto={Boolean(rotuloSeleccionado)}
        titulo="Rotulos de mercaderia"
        subtitulo={rotuloSeleccionado?.codigoTracking || ""}
        size="wide"
        onClose={cerrarRotuloModal}
        footer={
          rotuloSeleccionado && (
            <button className="btn" type="button" onClick={cerrarRotuloModal}>
              Cerrar
            </button>
          )
        }
      >
        {rotuloSeleccionado && (
          <div className="rotulos-print-area">
            <div className="rotulos-grid">
              <RotuloIngreso
                ingreso={rotuloSeleccionado}
                qrDataUrl={rotuloQrDataUrl}
                onImprimir={imprimirRotulo}
              />
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function DetalleDato({ label, value }) {
  return (
    <div className="detail-item">
      <span>{label}</span>
      <strong>{value === undefined || value === null || value === "" ? "-" : value}</strong>
    </div>
  );
}

function RotuloIngreso({ ingreso, qrDataUrl, onImprimir }) {
  const codigo = ingreso.codigoTracking || "-";
  const codigoBarras = codigoBarrasDataUrl(codigo);
  const cantidad = Number(ingreso.cantidadRecibida || 0);
  const unidad = obtenerUnidadCantidad(ingreso.unidadCantidad);

  return (
    <section className="rotulo-card">
      <div className="rotulo-actions">
        <button className="btn btn-primary" type="button" onClick={onImprimir}>
          <Printer size={16} />
          Imprimir
        </button>
      </div>
      <div className="rotulo-header">
        <div>
          <span>Ingreso de envases / materias primas</span>
          <strong>{codigo}</strong>
        </div>
        <div className="rotulo-type">Almacenamiento</div>
      </div>

      <div className="rotulo-product">
          <span>{ingreso.tipoIngreso === "material" ? "Material" : "Materia prima"}</span>
        <strong>{ingreso.producto || "-"}</strong>
      </div>

      <div className="rotulo-data-grid">
        <RotuloDato label="Proveedor" value={ingreso.proveedor || "-"} />
        <RotuloDato label="Lote / partida" value={ingreso.lotePartidaNro || "-"} />
        <RotuloDato label="Ingreso" value={formatearFecha(ingreso.fechaIngreso)} />
        <RotuloDato label="Elaboracion" value={formatearFecha(ingreso.fechaElaboracion)} />
        <RotuloDato label="Vencimiento" value={formatearFecha(ingreso.fechaVencimiento)} />
        <RotuloDato label="Ubicacion" value={ingreso.ubicacionAsignada || "-"} />
        <RotuloDato label="Cantidad" value={cantidad.toLocaleString("es-AR")} />
        <RotuloDato label="Unidad" value={unidad} />
      </div>

      <div className="rotulo-codes">
        <div className="rotulo-barcode">
          <img src={codigoBarras} alt={`Codigo de barras ${codigo}`} />
          <strong>{codigo}</strong>
        </div>
        <div className="rotulo-qr">
          {qrDataUrl ? <img src={qrDataUrl} alt={`QR tracking ${codigo}`} /> : <span>QR</span>}
          <small>Tracking</small>
        </div>
      </div>
    </section>
  );
}

function RotuloDato({ label, value }) {
  return (
    <div className="rotulo-dato">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div className="field">
      <label>{label}</label>
      <input type="text" {...props} />
    </div>
  );
}

function SelectField({ label, options, ...props }) {
  return (
    <div className="field">
      <label>{label}</label>
      <select {...props}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </select>
    </div>
  );
}

function Textarea({ label, ...props }) {
  return (
    <div className="field textarea-field">
      <label>{label}</label>
      <textarea {...props} />
    </div>
  );
}

const getOptionValue = (option) => (typeof option === "string" ? option : option.value);
const getOptionLabel = (option) => (typeof option === "string" ? option : option.label);

function SearchableSelect({ label, value, options, onChange, placeholder = "Buscar...", disabled = false }) {
  const [texto, setTexto] = useState("");
  const [abierto, setAbierto] = useState(false);
  const seleccion = options.find((option) => String(getOptionValue(option)) === String(value || ""));
  const query = texto.trim().toLowerCase();
  const filtradas = options
    .filter((option) => getOptionLabel(option).toLowerCase().includes(query))
    .slice(0, 12);

  useEffect(() => {
    setTexto(seleccion ? getOptionLabel(seleccion) : "");
  }, [value, seleccion]);

  const elegir = (option) => {
    setTexto(getOptionLabel(option));
    setAbierto(false);
    onChange(getOptionValue(option), option);
  };

  return (
    <div className="field searchable-select">
      <label>{label}</label>
      <input
        type="text"
        value={texto}
        placeholder={placeholder}
        disabled={disabled}
        onFocus={() => !disabled && setAbierto(true)}
        onBlur={() => window.setTimeout(() => setAbierto(false), 120)}
        onChange={(event) => {
          setTexto(event.target.value);
          setAbierto(true);
          onChange("", null);
        }}
        autoComplete="off"
      />
      {abierto && !disabled && (
        <div className="searchable-options">
          {filtradas.length === 0 ? (
            <div className="searchable-empty">Sin resultados</div>
          ) : (
            filtradas.map((option) => (
              <button
                key={getOptionValue(option)}
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => elegir(option)}
              >
                {getOptionLabel(option)}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function SearchFilter({ value, onChange, options, placeholder, ariaLabel, todosLabel }) {
  const [abierto, setAbierto] = useState(false);
  const query = normalizarBusqueda(value);
  const filtradas = options
    .filter((option) => normalizarBusqueda(option).includes(query))
    .slice(0, 12);

  const elegir = (option) => {
    onChange(option);
    setAbierto(false);
  };

  return (
    <div className="search searchable-select ingress-filter-search">
      <Search size={17} />
      <input
        type="search"
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel}
        onFocus={() => setAbierto(true)}
        onBlur={() => window.setTimeout(() => setAbierto(false), 120)}
        onChange={(event) => {
          onChange(event.target.value);
          setAbierto(true);
        }}
        autoComplete="off"
      />
      {abierto && (
        <div className="searchable-options">
          <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => elegir("")}>
            {todosLabel}
          </button>
          {filtradas.map((option) => (
            <button
              key={option}
              type="button"
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => elegir(option)}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
